import React from 'react';
import { addToCart } from '../../services/cart';
import { Product, ProductItem } from '../../api-client';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaShoppingCart } from "react-icons/fa";
import { ReactComponent as ShopBtn } from '../../assets/svg/Asset 42.svg';
import { fetchProductDetail } from '../../api/productDetail';
import { AddToCart } from '../../api-client/models'
import type { GuestCartProductData } from '../../services/cart';

interface BaseCartButtonProps {
  product: Product;
  quantity: number;
  selectedSizeId: number | null;
  selectedColorId: number | null;
  stock: number;
  onSuccess?: (colorId: number, sizeId: number, newStock: number) => void;
  buttonText: string;
  variant?: 'primary' | 'secondary';
}

export const BaseCartButton: React.FC<BaseCartButtonProps> = ({
  product,
  quantity,
  selectedSizeId,
  selectedColorId,
  stock,
  onSuccess,
  buttonText,
  variant = 'primary',
}) => {
  const handleClick = async () => {
    if (selectedSizeId === null || selectedColorId === null) {
      toast.warn('Please select a size and color before proceeding.' );
      return;
    }

    const productItem = product.product_items.find(
      (item) =>
        item.distribution?.color?.id === selectedColorId &&
        item.distribution?.size?.id === selectedSizeId
    );

    if (!productItem) {
      toast.warn('The selected size and color combination is not available.' );
      return;
    }

    if (stock <= 0) {
      toast.error('Sorry, there is no stock available for this combination.' );
      return;
    }

    if (quantity > stock) {
      toast.error(`Sorry, only ${stock} items available.` );
      return;
    }

   const cartOperation: AddToCart = {
      product_item_id: productItem.id!,
      quantity: quantity,
    };

    const guestCartProductData: GuestCartProductData = {
      product_name: product.name,
      price: Number(product.price),
      color: productItem.distribution?.color?.name || '',
      size: productItem.distribution?.size?.name || '',
      image: productItem.images?.[0]?.image || product.image || '',
    };

    if (quantity <= 0) {
      toast.warn('Please add at least one item.' );
      return;
    }

    try {
      await addToCart(cartOperation, guestCartProductData);
      
      const newStock = stock - quantity;
      
      if (onSuccess && selectedColorId && selectedSizeId) {
      onSuccess(selectedColorId, selectedSizeId, newStock);
      }
      
      toast.success('Product added to cart');
    } catch (error: any) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('stock')) {
      try {
        if (product.id !== undefined) {
        const updatedProduct = await fetchProductDetail(product.id);
        const updatedItem: ProductItem | undefined = updatedProduct.product_items.find(
          (item: ProductItem) => 
          item.distribution?.color?.id === selectedColorId && 
          item.distribution?.size?.id === selectedSizeId
        );
        
        if (updatedItem && onSuccess && selectedColorId && selectedSizeId) {
          onSuccess(
          selectedColorId, 
          selectedSizeId, 
          updatedItem.distribution?.stock ?? 0
          );
        }
        } else {
        throw new Error('Product ID is undefined');
        }
      } catch (fetchError) {
        console.error('Error fetching updated stock:', fetchError);
      }
      }
      
      toast.error(
      error.response?.data?.detail || 'Error adding to cart. Please try again.'
      );
    }
  };

  const isDisabled = !selectedSizeId || !selectedColorId || stock <= 0;

  const baseStyles = "flex w-32 h-10 items-center justify-center cursor-pointer transition-colors duration-200";
  const roundedStyles = variant === 'secondary' ? 'rounded-lg' : 'rounded-full';
  const primaryStyles = `bg-red-600 ${!isDisabled ? 'hover:bg-red-700' : 'bg-gray-400 cursor-not-allowed'}`;
  const secondaryStyles = `bg-white border-2 border-red-600 ${!isDisabled ? 'hover:bg-gray-100' : 'bg-gray-200 cursor-not-allowed'}`;
  const textPrimary = `font-custom font-bold text-sm text-center ${isDisabled ? 'text-gray-700' : 'text-white'}`;
  const textSecondary = `font-custom font-bold text-sm text-center ${isDisabled ? 'text-gray-700' : 'text-red-600'}`;

  const iconClass = variant === 'primary' ? 'text-white' : 'text-red-600';

  return (
    <div className="relative flex items-center">
      <div
        className={`${baseStyles} ${roundedStyles} ${variant === 'primary' ? primaryStyles : secondaryStyles} hidden lg:flex`}
        onClick={isDisabled ? undefined : handleClick}
        aria-label={buttonText}
      >
        {variant === 'primary' ? (
          <ShopBtn width={20} className='mr-2'/>
        ) : (
          <FaShoppingCart className={`mr-2 ${iconClass}`} size={20} />
        )}
        <p className={variant === 'primary' ? textPrimary : textSecondary}>
          {buttonText}
        </p>
      </div>

      <div
        className={`flex flex-row items-center lg:hidden p-2 ${roundedStyles} transition-colors duration-200 ${
          isDisabled
            ? 'cursor-not-allowed opacity-50'
            : variant === 'primary'
            ? 'bg-red-600 hover:bg-red-700'
            : 'bg-white border-2 border-red-600 hover:bg-gray-100 cursor-pointer'
        }`}
        onClick={isDisabled ? undefined : handleClick}
        aria-label={buttonText}
      >
        {variant === 'primary' ? (
          <ShopBtn width={20} className='mr-1'/>
        ) : (
          <FaShoppingCart className={`mr-2 ${iconClass}`} size={20} />
        )}
        <span
          className={`ml-2 font-custom font-bold text-sm ${
            variant === 'primary' ? 'text-white' : 'text-red-600'
          }`}
        >
          {buttonText}
        </span>
      </div>
    </div>
  );
};
